import { useAuthState } from '../../services/auth';
import { useContext, useEffect, useState } from 'react';
import './home.scss';
import { WidgetBox } from '../../components/box-widget/WidgetBox';
import axios from 'axios';
import { WidgetChart } from '../../components/widget-chart/WidgetChart';
import { TableWidget } from '../../components/table-widget/TableWidget';
import { BreakdownList } from '../../components/breakdown-list/BreakdownList';
import { useNavigate } from 'react-router-dom';
import { OperatorsContext, useOperatorsState } from '../../services/OperatorsList';
import { Link } from 'react-router-dom';
import { useEnterpriseStore } from '../../stores/enterprise.store';
import { ConsoleView } from 'react-device-detect';
import Pagination from 'rc-pagination';
import CountUp from 'react-countup';
import {
  ArrowRight,
  Check,
  ChevronLeft,
  ChevronRight,
  Globe,
  LayoutDashboard,
  MonitorSmartphone,
  Router,
  Search,
  ShieldCheck,
  X,
} from 'lucide-react';
import '../../design-system/index.css';
import { useToast } from '../../services/ToastContext';
import { Trans, useTranslation } from 'react-i18next';
import { LoadingSpinner } from '../../components/loading-spinner/LoadingSpinner';

const appNames = ['dde', 'mongo', 'mqtt', 'redis'];

export const Home = (props: any) => {
  const { token } = useAuthState();
  const { operatorList, loggedUserData } = useOperatorsState();
  const enterprise = useEnterpriseStore((state) => state.enterprise);
  const [summaryData, setSummaryData] = useState<any>();
  const [subOperatorsData, setSubOperatorsData] = useState<any[]>([]);
  const loaded = useEnterpriseStore((state) => state.loaded);
  const navigate = useNavigate();
  const [retinaDns, setRetinaDns] = useState<boolean>(false);
  const { showToastMessage } = useToast();
  const { t } = useTranslation();

  // Enhanced state management for data
  const [allData, setAllData] = useState<any[]>([]); // Store ALL data from API
  const [pagedData, setPagedData] = useState<any[]>([]); // Current page data
  const [filteredData, setFilteredData] = useState<any[]>([]); // Filtered results
  const [displayData, setDisplayData] = useState<any[]>([]); // Data to display (paginated filtered results)
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [pagination, setPagination] = useState({
    page: 1,
    size: 8, // Better for grid layouts
    total: 0,
  });

  const [userEmail, setUserEmail] = useState<string | null>();
  const [loadingToken, setLoadingToken] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<number>();
  const [selectedId, setSelectedId] = useState<number>();
  const [isMaster, setIsMaster] = useState<any>(null);
  const [masterOperatorId, setMasterOperatorId] = useState<any>(null);
  const [cpeData, setCpeData] = useState<number>(0);
  const [deviceData, setDeviceData] = useState<number>(0);
  const [threatData, setThreatData] = useState<number>(0);
  const [dnsData, setDnsData] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [retinaFilter, setRetinaFilter] = useState('');
// Enhanced state for incremental loading
const [loadingState, setLoadingState] = useState({
  currentSize: 500, // Start with pageSize
  totalRecords: 0,
  currentFetchedCount: 0
});

// NEW: Function to fetch operators data with incremental loading
async function fetchAllSubOperatorsData() {
  if (!userEmail) return;

  setIsLoading(true);
  try {
    let allOperators: any[] = [];
    let currentPage = 1;
    const pageSize = 500;
    let hasMoreData = true;
    let totalRecords = 0;

    // Fetch data incrementally
    while (hasMoreData) {
      const response = await axios.get(
        `/v2/op/master-operator/${userEmail}/sub-operators`,
        {
          params: { 
            page: currentPage, 
            size: loadingState.currentSize // Use currentSize from loadingState
          },
        }
      );

      const result = response.data;

      if (result.data && result.data.length > 0) {
        allOperators = [...allOperators, ...result.data];
        totalRecords = result.totalRecords || result.total || allOperators.length;
        
        // Update loading state with current fetch count
        setLoadingState(prev => ({
          ...prev,
          currentFetchedCount: allOperators.length,
          totalRecords: totalRecords
        }));

        // Check if we need to fetch more data incrementally
        if (allOperators.length === loadingState.currentSize && allOperators.length < totalRecords) {
          // Increment the page size for next fetch
          setLoadingState(prev => ({ 
            ...prev, 
            currentSize: prev.currentSize + pageSize 
          }));
          currentPage++;
        } else {
          hasMoreData = false;
        }
      } else {
        hasMoreData = false;
      }

      // Safety check to prevent infinite loops
      if (currentPage > 100) {
        console.warn('Safety break: Too many pages fetched');
        hasMoreData = false;
      }
    }

    setAllData(allOperators);
    setFilteredData(allOperators);

    // Update pagination
    setPagination(prev => ({
      ...prev,
      page: 1,
      total: allOperators.length
    }));

    console.log(`Fetched ${allOperators.length} operators total out of ${totalRecords} records`);

  } catch (error) {
    console.error('Error fetching all sub-operators data:', error);
    showToastMessage(t('master.toasts.loadFailed'), 'error');
  } finally {
    setIsLoading(false);
  }
}

// Alternative approach: More similar to your reference pattern
async function fetchAllSubOperatorsDataAlternative() {
  if (!userEmail) return;

  setIsLoading(true);
  try {
    const response = await axios.get(
      `/v2/op/master-operator/${userEmail}/sub-operators`,
      {
        params: { 
          page: 1, 
          size: loadingState.currentSize 
        },
      }
    );

    const result = response.data;
    const fetchedData = result.data || [];
    const totalRecords = result.totalRecords || result.total || 0;

    setAllData(fetchedData);
    setFilteredData(fetchedData);

    // Update loading state
    setLoadingState(prev => ({
      ...prev,
      currentFetchedCount: fetchedData.length,
      totalRecords: totalRecords
    }));

    // Implement the incremental loading logic
    if (fetchedData.length === loadingState.currentSize && fetchedData.length < totalRecords) {
      // Increment size for next potential fetch
      setLoadingState(prev => ({ 
        ...prev, 
        currentSize: prev.currentSize + 500 // Increase by pageSize
      }));
      
      // Optionally: Auto-fetch next batch or wait for user action
      console.log(`More data available. Current: ${fetchedData.length}, Total: ${totalRecords}`);
    }

    setPagination(prev => ({
      ...prev,
      page: 1,
      total: fetchedData.length
    }));

    console.log(`Fetched ${fetchedData.length} operators`);

  } catch (error) {
    console.error('Error fetching sub-operators data:', error);
    showToastMessage(t('master.toasts.loadFailed'), 'error');
  } finally {
    setIsLoading(false);
  }
}

// Load more data function for manual triggering
const loadMoreData = async () => {
  if (!userEmail || isLoading) return;

  setIsLoading(true);
  try {
    const nextPage = Math.floor(loadingState.currentFetchedCount / 500) + 1;
    
    const response = await axios.get(
      `/v2/op/master-operator/${userEmail}/sub-operators`,
      {
        params: { 
          page: nextPage, 
          size: 500 
        },
      }
    );

    const result = response.data;
    const newData = result.data || [];

    if (newData.length > 0) {
      const updatedData = [...allData, ...newData];
      
      setAllData(updatedData);
      setFilteredData(updatedData);

      setLoadingState(prev => ({
        ...prev,
        currentFetchedCount: updatedData.length,
        currentSize: prev.currentSize + 500
      }));

      setPagination(prev => ({
        ...prev,
        total: updatedData.length
      }));

      console.log(`Loaded ${newData.length} more operators. Total: ${updatedData.length}`);
    }

  } catch (error) {
    console.error('Error loading more data:', error);
    showToastMessage(t('master.toasts.loadMoreFailed'), 'error');
  } finally {
    setIsLoading(false);
  }
};

// Effect to trigger incremental loading when currentSize changes
useEffect(() => {
  if (loadingState.currentSize > 500 && loadingState.currentFetchedCount < loadingState.totalRecords) {
    fetchAllSubOperatorsData();
  }
}, [loadingState.currentSize]);
  // Needs both the auth token and the email (read from localStorage in a sibling
  // effect). On client-side navigation the token is already set at mount, so this
  // must also re-run once `userEmail` arrives — otherwise the list stays empty.
  useEffect(() => {
    if (token && userEmail) {
      fetchAllSubOperatorsData();
      fetchWidgetData();
    }
  }, [token, userEmail]);

  const getEmailFromLocalStorage = (): string | null => {
    const email = localStorage.getItem('email');
    setUserEmail(email);
    return email ? email : null;
  };

  useEffect(() => {
    const email = getEmailFromLocalStorage();
    if (email) {
      setUserEmail(email);
      fetchMasterStatus(email);
    }
  }, []);


  // Enhanced filter function that works on ALL data
  useEffect(() => {
    let filtered = allData;

    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (row) =>
          row.Name?.toLowerCase().includes(query) ||
          row.Domain?.toLowerCase().includes(query) ||
          row.ID?.toString().includes(query) || // Add operator ID search
          row['Operator Key']?.toString().toLowerCase().includes(query)
      );
    }

    if (retinaFilter !== '') {
      const active = retinaFilter === 'true';
      filtered = filtered.filter(
        (row) => row['Retina Visibility'] === active
      );
    }

    setFilteredData(filtered);

    // Reset to first page when filters change
    setPagination(prev => ({
      ...prev,
      page: 1,
      total: filtered.length
    }));
  }, [searchQuery, retinaFilter, allData]);

  // Paginate the filtered data
  useEffect(() => {
    const startIndex = (pagination.page - 1) * pagination.size;
    const endIndex = startIndex + pagination.size;
    const paginatedResults = filteredData.slice(startIndex, endIndex);
    setDisplayData(paginatedResults);
  }, [filteredData, pagination.page, pagination.size]);

  const handlePageChange = (page: number, pageSize?: number) => {
    setPagination(prev => ({
      ...prev,
      page,
      size: pageSize || prev.size
    }));
  };

  function convertToBreakdownList(summaryData: any[]) {
    return summaryData?.map((item: any) => {
      return { key: item.type, value: t('master.breakdown.devices', { count: item.count }) };
    });
  }

  async function goToOperatorPage(rowData: any) {
    try {
      const operatorKey =
        rowData?.['Operator Key'] ||
        rowData?.key?.['Operator Key'] ||
        rowData?.OperatorKey ||
        rowData?.operatorKey;

      if (!operatorKey) {
        console.error('Operator Key not found in row data:', rowData);
        return;
      }

      setSelectedKey(operatorKey);
      await fetchToken(operatorKey);
      window.localStorage.setItem('currentNav', 'Overview');
      navigate(`/operators/${operatorKey}`);
      console.log(`Navigated to operator ${operatorKey} successfully.`);
    } catch (error) {
      console.error('Error navigating to operator page:', error);
    }
  }

  const fetchMasterStatus = async (email: any) => {
    try {
      const response = await axios.get(`/v2/op/master-operator/master?email=${email}`);
      setIsMaster(response.data.masterControl);
      const operatorId = response.data.operatorId.toLocaleString();
      setMasterOperatorId(operatorId);
      return operatorId;
    } catch (error) {
      console.error('Error fetching master status:', error);
    }
  };

  const updateId = (operatorId: any) => {
    const setLoggedUserData = `{"email": "${userEmail}", "operatorId": "${operatorId}"}`;
    localStorage.setItem('loggedUserData', setLoggedUserData);
    console.log('Updated loggedUserData in localStorage');
  };

  const fetchToken = async (operatorId: string) => {
    const existingToken = localStorage.getItem('loginToken');
    if (!existingToken) {
      console.error('No token found in Local Storage');
      return;
    }
    try {
      setLoadingToken(true);
      const requestBody = {
        email: userEmail,
        operatorKey: Number(operatorId),
        operatorId: Number(operatorId),
      };
      const response = await axios.post('v2/op/master-operator/master-token', requestBody);
      const newToken = response.data.token;
      localStorage.setItem('loginToken', newToken);
      updateId(operatorId);
      console.log('Token updated successfully');
    } catch (error) {
      setError('Failed to update Token');
      console.error('Error fetching token:', error);
    } finally {
      setLoadingToken(false);
    }
  };

  const navigateCpe = async () => {
    try {
      const operatorId = await fetchMasterStatus(userEmail);
      if (operatorId) {
        await fetchToken(operatorId);
        window.localStorage.setItem('currentNav', "Accounts");
        const cpePage = `/general-list/${operatorId}/operatorcpe/Total Accounts Managed`;
        navigate(cpePage);
      } else {
        console.error('Master Operator ID not available, navigation aborted');
      }
    } catch (error) {
      console.error('Error during navigation:', error);
    }
  };

  const navigateDevices = async () => {
    try {
      const operatorId = await fetchMasterStatus(userEmail);
      if (operatorId) {
        await fetchToken(operatorId);
        window.localStorage.setItem('currentNav', "Devices");
        const cpePage = `/general-list/${operatorId}/operatordevices/Total Devices Managed`;
        navigate(cpePage);
      } else {
        console.error('Master Operator ID not available, navigation aborted');
      }
    } catch (error) {
      console.error('Error during navigation:', error);
    }
  };

  const navigateThreats = async () => {
    try {
      const operatorId = await fetchMasterStatus(userEmail);
      if (operatorId) {
        await fetchToken(operatorId);
        window.localStorage.setItem('currentNav', "Threats");
        const cpePage = `/threatsmanaged/${operatorId}`;
        navigate(cpePage);
      } else {
        console.error('Master Operator ID not available, navigation aborted');
      }
    } catch (error) {
      console.error('Error during navigation:', error);
    }
  };

  const navigateDns = async () => {
    try {
      const operatorId = await fetchMasterStatus(userEmail);
      if (operatorId) {
        await fetchToken(operatorId);
        window.localStorage.setItem('currentNav', "DNS Activity");
        const cpePage = `/dnsactivity/${operatorId}`;
        navigate(cpePage);
      } else {
        console.error('Master Operator ID not available, navigation aborted');
      }
    } catch (error) {
      console.error('Error during navigation:', error);
    }
  };

  const fetchWidgetData = async () => {
    try {
      const operatorId = await fetchMasterStatus(userEmail);
      if (operatorId) {
        const response = await axios.get(`/v2/op/master-operator/${operatorId}/global-counts`);
        setCpeData(response.data.data[0].cpeCount || 0);
        setDeviceData(response.data.data[0].devicesCount || 0);
        setThreatData(response.data.data[0].past24HourThreats || 0);
        setDnsData(response.data.data[0].past24HourDns || 0);
      } else {
        console.error('Master Operator ID not available, navigation aborted');
      }
    } catch (error) {
      console.error('Error fetching widget data:', error);
    }
  };

  // Enhanced toggle function that works with allData
  const handleRetinaDnsToggle = async (customerId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const currentRow = allData.find((row) => row.ID === customerId);
      const newValue = !currentRow?.['Retina Visibility'];

      // Optimistic update to allData
      setAllData((prev) =>
        prev.map((row) =>
          row.ID === customerId ? { ...row, 'Retina Visibility': newValue } : row
        )
      );

      await axios.patch(
        `/v2/op/master-operator/update-retina-dns-visibility`,
        {},
        {
          params: {
            visible: newValue,
            operatorId: customerId,
          },
        }
      );

      console.log(`Retina DNS visibility updated for operator ${customerId} → ${newValue}`);
      showToastMessage(t('master.toasts.retinaUpdated'), 'success');
      window.location.reload();
    } catch (error) {
      console.error('Error updating Retina DNS visibility:', error);
      // Revert optimistic update on error
      setAllData((prev) =>
        prev.map((row) =>
          row.ID === customerId
            ? { ...row, 'Retina Visibility': !row['Retina Visibility'] }
            : row
        )
      );
      showToastMessage(t('master.toasts.retinaUpdateFailed'), 'error');
    }
  };

  // Risk score → design-system chip tone
  const getRiskTone = (score: number) => {
    if (score >= 80) return 'danger';
    if (score >= 40) return 'warn';
    return 'mint';
  };

  const kpis = [
    {
      title: t('master.kpi.cpes'),
      value: cpeData,
      icon: Router,
      href: `/general-list/${masterOperatorId}/operatorcpe/Total Accounts Managed`,
      onClick: navigateCpe,
    },
    {
      title: t('master.kpi.devices'),
      value: deviceData,
      icon: MonitorSmartphone,
      href: `/general-list/${masterOperatorId}/operatordevices/Total Devices Managed`,
      onClick: navigateDevices,
    },
    {
      title: t('master.kpi.dnsRequests'),
      value: dnsData,
      icon: Globe,
      href: `/dnsactivity/${masterOperatorId}`,
      onClick: navigateDns,
    },
    {
      title: t('master.kpi.threatsBlocked'),
      value: threatData,
      icon: ShieldCheck,
      href: `/threatsmanaged/${loggedUserData?.operatorId}`,
      onClick: navigateThreats,
    },
  ];

  const retinaFilters = [
    { value: '', label: t('master.operators.filters.all') },
    { value: 'true', label: t('master.operators.filters.active') },
    { value: 'false', label: t('master.operators.filters.inactive') },
  ];
  const avatarColors = ['', 'blue', 'violet', 'amber', 'slate'];

  return (
    <div className="bd home-page" data-theme={props.darkMode ? 'dark' : 'light'} data-accent="mint">
      <section id="home">
        <header className="home-hd">
          <div className="home-hd-text">
            <span className="home-eyebrow">{t('master.eyebrow')}</span>
            <h1 className="bd-h1 home-title">{t('master.title')}</h1>
            <span className="bd-body">{t('master.subtitle')}</span>
          </div>
          <Link to="/manager/dashboard" className="bd-pill home-manager">
            <LayoutDashboard size={15} />
            {t('master.managerDashboard')}
            <ArrowRight size={14} />
          </Link>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {kpis.map(({ title, value, icon: Icon, href, onClick }) => (
            <a
              key={title}
              href={href}
              className="bd-card home-kpi"
              onClick={(e) => {
                e.preventDefault();
                onClick();
              }}
            >
              <div className="home-kpi-hd">
                <span className="home-kpi-icon">
                  <Icon size={18} strokeWidth={1.75} />
                </span>
                <span className="home-kpi-label">{title}</span>
              </div>
              <CountUp className="bd-stat-num" preserveValue={true} end={value || 0} duration={2} separator="," />
              <span className="home-kpi-link">
                {t('master.kpi.viewDetails')}
                <ArrowRight size={13} />
              </span>
            </a>
          ))}
        </div>

        <div className="charts-container">
          {summaryData && (
            <WidgetChart type="line" icon="/icons/cube.svg" title={t('master.charts.devicesPerOperator')} data={summaryData?.operatorSummary} />
          )}
          {summaryData && (
            <WidgetChart
              dataPointClick={true}
              type="bar"
              icon="/icons/shield-dark.svg"
              title={t('master.charts.threatsBlocked')}
              data={summaryData?.threatArray}
            />
          )}
          {summaryData && <WidgetChart type="donut" icon="/icons/pc-grey.svg" title={t('master.charts.devicesByType')} data={summaryData?.summary} />}
        </div>

        {loggedUserData?.operatorId !== undefined && (
          <div className="bd-card operators-cards-container">
            <header className="ops-hd">
              <div className="ops-title">
                <h2 className="bd-h3">{t('master.operators.title')}</h2>
                <span className="bd-meta">{t('master.operators.subtitle')}</span>
                {isLoading && (
                  <span className="bd-meta ops-loading">
                    <span className="ops-spinner" />
                    {t('master.operators.loadingData')}
                  </span>
                )}
              </div>

              {/* 🔍 Filters Section */}
              <div className="ops-tools">
                <label className="bd-search">
                  <Search size={15} />
                  <input
                    type="text"
                    placeholder={t('master.operators.searchPlaceholder')}
                    aria-label={t('master.operators.searchAriaLabel')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setSearchQuery('');
                    }}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="bd-search-clear"
                      aria-label={t('master.operators.clearSearch')}
                      onClick={(e) => {
                        setSearchQuery('');
                        e.currentTarget.parentElement?.querySelector('input')?.focus();
                      }}
                    >
                      <X size={12} strokeWidth={2.5} />
                    </button>
                  )}
                </label>
                <div className="bd-segmented" role="group" aria-label={t('master.operators.filterAriaLabel')}>
                  {retinaFilters.map((filter) => (
                    <button
                      key={filter.value}
                      type="button"
                      className={retinaFilter === filter.value ? 'active' : ''}
                      aria-pressed={retinaFilter === filter.value}
                      onClick={() => setRetinaFilter(filter.value)}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
              </div>
            </header>

            {/* Results Summary */}
            {filteredData.length > 0 && (
              <div className="ops-summary bd-meta">
                <Trans
                  i18nKey="master.operators.showing"
                  count={filteredData.length}
                  values={{ shown: displayData.length, total: filteredData.length }}
                  components={{ num: <span className="bd-mono" /> }}
                />
                {filteredData.length !== allData.length && ` ${t('master.operators.filteredFrom', { total: allData.length })}`}
              </div>
            )}

            <div className="ops-body">
              {isLoading ? (
                <LoadingSpinner text={t('master.operators.loading')} />
              ) : displayData.length === 0 ? (
                <div className="ops-empty">
                  {allData.length === 0 ? t('master.operators.empty') : t('master.operators.noMatch')}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {displayData.map((operator) => {
                    const isCurrentOperator = Number(operator.ID) === Number(loggedUserData?.operatorId);
                    const retinaActive = !!operator['Retina Visibility'];

                    return (
                      <div
                        key={operator.ID}
                        className={`operator-card ${isCurrentOperator ? 'current' : ''}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => goToOperatorPage(operator)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') goToOperatorPage(operator);
                        }}
                      >
                        {/* Card Header */}
                        <div className="op-card-hd">
                          <span className={`bd-avatar ${avatarColors[operator.ID % avatarColors.length]}`}>
                            {(operator.Name || '?').charAt(0).toUpperCase()}
                          </span>
                          <div className="op-card-id">
                            <h3 className="op-card-name" title={operator.Name}>{operator.Name}</h3>
                            <span className="bd-meta op-card-domain" title={operator.Domain}>{operator.Domain}</span>
                          </div>
                          {isCurrentOperator ? (
                            <span className="bd-chip mint">
                              <Check size={12} strokeWidth={2.5} />
                              {t('master.operators.current')}
                            </span>
                          ) : (
                            <span className="bd-tag">#{operator.ID}</span>
                          )}
                        </div>

                        {/* Card Body */}
                        <div className="op-card-rows">
                          <div className="op-card-row">
                            <span className="op-card-label">{t('master.operators.operatorId')}</span>
                            <span className="bd-mono op-card-value">#{operator.ID}</span>
                          </div>
                          <div className="op-card-row">
                            <span className="op-card-label">{t('master.operators.gracePeriod')}</span>
                            <span className="op-card-value">{operator['Grace Period']}</span>
                          </div>
                          <div className="op-card-row">
                            <span className="op-card-label">{t('master.operators.riskScore')}</span>
                            <span className={`bd-chip bd-mono ${getRiskTone(operator['Risk Score'])}`}>
                              {operator['Risk Score']}
                            </span>
                          </div>
                        </div>

                        {/* Activity Status with Switch */}
                        <div className="op-card-activity">
                          <div className="op-card-id">
                            <span className="op-card-activity-title">{t('master.operators.activityStatus')}</span>
                            <span className="bd-meta">
                              {retinaActive ? t('master.operators.retinaActive') : t('master.operators.retinaInactive')}
                            </span>
                          </div>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={retinaActive}
                            aria-label={t('master.operators.retinaToggleAriaLabel', { name: operator.Name })}
                            className={`bd-switch ${retinaActive ? 'on' : ''}`}
                            onClick={(e) => handleRetinaDnsToggle(operator.ID, e)}
                            onKeyDown={(e) => e.stopPropagation()}
                          />
                        </div>

                        {/* Card Footer */}
                        <div className="op-card-ft">
                          {t('master.operators.viewDetails')}
                          <ArrowRight size={14} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Pagination */}
            {filteredData.length > 0 && (
              <div className="ops-ft">
                <span className="bd-meta">
                  <Trans
                    i18nKey="master.operators.pageOf"
                    values={{ page: pagination.page, pages: Math.ceil(filteredData.length / pagination.size) }}
                    components={{ num: <span className="bd-mono" /> }}
                  />
                </span>
                <Pagination
                  className="pagination bd-pagination"
                  current={pagination.page}
                  pageSize={pagination.size}
                  total={filteredData.length}
                  onChange={handlePageChange}
                  nextIcon={<ChevronRight size={16} />}
                  prevIcon={<ChevronLeft size={16} />}
                />
              </div>
            )}
          </div>
        )}

        <BreakdownList
          title={t('master.breakdown.title')}
          subtitle={t('master.breakdown.subtitle')}
          icon="/icons/info-icon.svg"
          data={convertToBreakdownList(summaryData?.summary)}
        />
      </section>
    </div>
  );
};

export default function CustomNextIcon() {
  return (
    <svg width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0.943787" y="0.5" width="31" height="31" rx="7.5" stroke="#BBCEDB" />
      <path
        d="M14.4438 20L18.1183 16.7071C18.5523 16.3182 18.5523 15.6818 18.1183 15.2929L14.4438 12"
        stroke="#BBCEDB"
        strokeWidth="2"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export const CustomPreviousIcon = () => (
  <svg width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="31.9438" y="31.5" width="31" height="31" rx="7.5" transform="rotate(-180 31.9438 31.5)" stroke="#BBCEDB" />
    <path
      d="M18.4438 12L14.7693 15.2929C14.3353 15.6818 14.3353 16.3182 14.7693 16.7071L18.4438 20"
      stroke="#BBCEDB"
      strokeWidth="2"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
